import AdsClick from "@mui/icons-material/AdsClick";
import Assignment from "@mui/icons-material/Assignment";
import Clear from "@mui/icons-material/Clear";
import FilterAlt from "@mui/icons-material/FilterAlt";
import ForkLeft from "@mui/icons-material/ForkLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import ListAlt from "@mui/icons-material/ListAlt";
import Refresh from "@mui/icons-material/Refresh";
import Sort from "@mui/icons-material/Sort";
import Stop from "@mui/icons-material/Stop";
import TouchApp from "@mui/icons-material/TouchApp";
import {
	Avatar,
	Box,
	ButtonBase,
	Divider,
	Paper,
	Popper,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import TrapFocus from "@mui/material/Unstable_TrapFocus";
import { useEffect, useRef } from "react";

import { useAddNodeMenu } from "./add-node-menu-context";
import { getAllowedNextNodeTypes } from "./node-adjacency";

const ALL_MENU_ITEMS = [
	{ key: "action",         label: "Action",         icon: <AdsClick fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)" },
	{ key: "assignment",     label: "Assignment",     icon: <Assignment fontSize="small" />,         color: "#06B097", gradient: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)" },
	{ key: "clear_reaction", label: "Clear Reaction", icon: <Clear fontSize="small" />,              color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)" },
	{ key: "decision",       label: "Decision",       icon: <ForkLeft fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)" },
	{ key: "event_listener", label: "Event Listener", icon: <TouchApp fontSize="small" />,           color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)" },
	{ key: "filter",         label: "Filter",         icon: <FilterAlt fontSize="small" />,          color: "#F35A35", gradient: "linear-gradient(135deg, #FF7A3C 0%, #EE3A2D 100%)" },
	{ key: "loop",           label: "Loop",           icon: <Refresh fontSize="small" />,            color: "#A434D9", gradient: "linear-gradient(135deg, #D63AD9 0%, #8C30E0 100%)" },
	{ key: "query_builder",  label: "Query Builder",  icon: <FormatListBulleted fontSize="small" />, color: "#3F77E8", gradient: "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)" },
	{ key: "reaction",       label: "Reaction",       icon: <ListAlt fontSize="small" />,            color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)" },
	{ key: "sort",           label: "Sort",           icon: <Sort fontSize="small" />,               color: "#06B097", gradient: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)" },
	{ key: "stop",           label: "Stop",           icon: <Stop fontSize="small" />,               color: "#D32E26", gradient: "linear-gradient(135deg, #E94136 0%, #B81E15 100%)" },
];

const gradStops = (g: string): string[] => g.match(/#[0-9a-fA-F]{3,8}/g) ?? [];
const gradId = (g: string): string => {
	const c = gradStops(g);
	return c.length >= 2 ? `anmGrad-${c[0].slice(1)}-${c[1].slice(1)}` : "";
};
const UNIQUE_GRADIENTS = Array.from(new Set(ALL_MENU_ITEMS.map((i) => i.gradient)));

interface AddNodeMenuProps {
	excludeTypes?: string[];
	nodes?: ReadonlyArray<{ id: string; type?: string }>;
}

const AddNodeMenu = ({ excludeTypes, nodes }: AddNodeMenuProps) => {
	const theme = useTheme();
	const { anchorEl, isOpen, sourceNodeId, closeMenu, selectItem } = useAddNodeMenu();

	const sourceType = sourceNodeId ? nodes?.find((n) => n.id === sourceNodeId)?.type : undefined;
	const allowedTypes = getAllowedNextNodeTypes(sourceType);

	const allowedKeys = allowedTypes === null ? null : new Set<string>(allowedTypes);
	const MENU_ITEMS = ALL_MENU_ITEMS.filter(
		(item) =>
			(allowedKeys === null || allowedKeys.has(item.key)) &&
			!excludeTypes?.includes(item.key),
	);

	const menuRef = useRef<HTMLDivElement>(null);
	const lastFocusedRef = useRef<HTMLElement | null>(null);
	useEffect(() => {
		if (isOpen && anchorEl instanceof HTMLElement) {
			lastFocusedRef.current = anchorEl;
		} else if (!isOpen && lastFocusedRef.current) {
			lastFocusedRef.current.focus();
			lastFocusedRef.current = null;
		}
	}, [isOpen, anchorEl]);

	/**
	 * Close on any outside pointerdown (capture phase).
	 * Bubble-phase ClickAwayListener misses canvas/other UI — ReactFlow stops propagation.
	 * Ignore clicks inside menu panel or on the opening anchor (+ toggle).
	 */
	useEffect(() => {
		if (!isOpen) return;

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (menuRef.current?.contains(target)) return;
			if (anchorEl instanceof HTMLElement && anchorEl.contains(target)) return;
			closeMenu();
		};

		document.addEventListener("pointerdown", handlePointerDown, true);
		return () => document.removeEventListener("pointerdown", handlePointerDown, true);
	}, [isOpen, anchorEl, closeMenu]);

	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				closeMenu();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [isOpen, closeMenu]);

	return (
		<Popper
			open={isOpen}
			anchorEl={anchorEl}
			placement="bottom"
			modifiers={[
				{ name: "offset", options: { offset: [0, 12] } },
				{
					name: "flip",
					enabled: true,
					options: {
						fallbackPlacements: ["top", "right", "left", "bottom-end", "top-end"],
						boundary: "viewport",
						padding: 16,
					},
				},
				{
					name: "preventOverflow",
					options: { boundary: "viewport", padding: 16, altAxis: true },
				},
			]}
			style={{ zIndex: theme.zIndex.modal + 1 }}
		>
			<TrapFocus open={isOpen} disableAutoFocus={false} disableRestoreFocus>
				<Paper
					ref={menuRef}
					elevation={6}
					tabIndex={-1}
					role="menu"
					aria-label="Add node to canvas"
					sx={{
						borderRadius: 3,
						minWidth: 260,
						maxWidth: 320,
						overflow: "hidden",
						p: 1.5,
						boxShadow: "0px 10px 32px rgba(0,0,0,0.12)",
						"& [role='menuitem']:focus-visible": {
							outline: `2px solid ${theme.palette.primary.main}`,
							outlineOffset: 2,
						},
					}}
				>
					<Stack spacing={1.25}>
							<svg width={0} height={0} style={{ position: "absolute" }} aria-hidden focusable="false">
								<defs>
									{UNIQUE_GRADIENTS.map((g) => {
										const c = gradStops(g);
										if (c.length < 2) return null;
										return (
											<linearGradient key={g} id={gradId(g)} x1="0" y1="0" x2="1" y2="1">
												<stop offset="0%" stopColor={c[0]} />
												<stop offset="100%" stopColor={c[1]} />
											</linearGradient>
										);
									})}
								</defs>
							</svg>
							<Box sx={{ px: 1, pt: 0.5 }}>
								<Typography variant="body2" color="text.secondary" sx={{fontWeight: 600}}>
								Add elements to canvas
								</Typography>
							</Box>

							<Divider />

							<Stack spacing={0.5}>
								{MENU_ITEMS.map((item) => (
									<ButtonBase
										key={item.key}
										role="menuitem"
										aria-label={`Add ${item.label} node`}
										onClick={() => selectItem(item.key)}
										sx={{
											width: "100%",
											textAlign: "left",
											justifyContent: "flex-start",
											borderRadius: 2,
											px: 1,
											py: 0.75,
											transition: "background-color 0.2s ease, box-shadow 0.2s ease",
											"&:hover": {
												backgroundColor: theme.palette.action.hover,
												boxShadow: theme.shadows[1],
											},
										}}
									>
										<Stack direction="row" spacing={1.25} sx={{alignItems: "center", justifyContent: "flex-start", width: "100%"}}>
											<Avatar
												variant="rounded"
												sx={{
													width: 34,
													height: 34,
													background: `${item.color}1F`,
													color: item.color,
													fontSize: 18,
													border: "none",
													boxShadow: "none",
													"& svg": { fill: `url(#${gradId(item.gradient)})` },
												}}
											>
												{item.icon}
											</Avatar>
											<Typography variant="body1" sx={{fontWeight: 700, color: "text.primary"}}>
												{item.label}
											</Typography>
										</Stack>
									</ButtonBase>
								))}
							</Stack>
					</Stack>
				</Paper>
			</TrapFocus>
		</Popper>
	);
};

export default AddNodeMenu;
